import React, { useMemo } from "react";
// Energy Consumption Overview

// Mui
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

// ECharts
import ReactECharts from "echarts-for-react";
import * as echarts from "echarts";

// Custom
import DashboardCard from "../card/DashboardCard";
import { chartData, metricsData } from "./SystemConsumptionData";

const EnergyOverview: React.FC = () => {
  return (
    <DashboardCard
      title={
        <Box>
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography variant="h6" fontWeight={700} noWrap>
              System Consumption by Hour (Last 7 Days)
            </Typography>
          </Stack>
          <Typography variant="body2" color="textSecondary" mt={0.5}>
            Aggregated kWh from ~10,000 smart meters • Endpoint AMI data
          </Typography>
        </Box>
      }
      className={"energy_overview"}
      showIcons={false}
      action={
        <Stack direction="row" spacing={3} alignItems="flex-start">
          {metricsData.map((item, index) => (
            <Box key={index} textAlign="right">
              <Typography
                variant="caption"
                color="textSecondary"
                display="block"
                mb={0.5}
                sx={{ whiteSpace: "nowrap" }}
              >
                {item.label}
              </Typography>
              <Stack
                direction="row"
                alignItems="baseline"
                justifyContent="flex-end"
                spacing={0.5}
              >
                <Typography
                  variant="h6"
                  fontWeight={700}
                  color={item.color}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {item.value.split(" ")[0]}
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  color={item.color}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {item.value.split(" ")[1]}
                </Typography>
              </Stack>

              {item.subLabel && (
                <Typography
                  variant="caption"
                  color="textSecondary"
                  display="block"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {item.subLabel}
                </Typography>
              )}
            </Box>
          ))}
        </Stack>
      }
      body={
        <Box
          marginTop={"20px"}
          width={"100%"}
          display="flex"
          flexDirection="column"
        >
          <Box height={"320px"} width={"100%"}>
            <SystemConsumptionChart />
          </Box>

          {/* Custom Days Footer */}
          <Box
            display="flex"
            justifyContent="space-between"
            px={"3%"} // Matches grid left/right
            mt={1}
            borderTop="1px solid #E5E7EB"
            pt={1}
          >
            <Box flex={1} textAlign="center">
              <Typography variant="caption" color="textSecondary">
                Mon
              </Typography>
            </Box>
            <Box flex={1} textAlign="center">
              <Typography variant="caption" color="textSecondary">
                Tue
              </Typography>
            </Box>
            <Box flex={1} textAlign="center">
              <Typography variant="caption" color="textSecondary">
                Wed
              </Typography>
            </Box>
            <Box flex={1} textAlign="center">
              <Typography variant="caption" color="textSecondary">
                Thu
              </Typography>
            </Box>
            <Box flex={1} textAlign="center">
              <Typography variant="caption" color="textSecondary">
                Fri
              </Typography>
            </Box>
            <Box flex={1} textAlign="center">
              <Typography
                variant="caption"
                color="textSecondary"
                display="block"
              >
                Sat
              </Typography>
              <Typography
                variant="caption"
                color="textSecondary"
                fontSize="10px"
              >
                Weekend
              </Typography>
            </Box>
            <Box flex={1} textAlign="center">
              <Typography
                variant="caption"
                color="textSecondary"
                display="block"
              >
                Sun
              </Typography>
              <Typography
                variant="caption"
                color="textSecondary"
                fontSize="10px"
              >
                Weekend
              </Typography>
            </Box>
          </Box>
        </Box>
      }
    />
  );
};

export default EnergyOverview;

const SystemConsumptionChart = () => {
  const options = useMemo(() => {
    return {
      tooltip: {
        trigger: "axis",
        formatter: (params: any) => {
          const param = params[0];
          return `${param.name}<br />Value: <b>${param.value.toLocaleString()} kWh</b>`;
        },
      },
      grid: {
        left: "3%",
        right: "3%",
        bottom: "5%",
        top: "20px",
        containLabel: true,
      },
      xAxis: [
        {
          type: "category",
          boundaryGap: false,
          data: chartData.dates,
          axisLabel: {
            interval: 5, // Show every 6th label
            formatter: (value: string) => {
              const [day, time] = value.split(" ");
              // Only show times 06:00, 12:00, 18:00.
              if (["06:00", "12:00", "18:00"].includes(time)) return time;
              return "";
            },
            color: "#9CA3AF",
            fontSize: 10,
            rotate: 45,
            margin: 10,
          },
          axisTick: { show: false },
          axisLine: { show: false },
        },
      ],
      yAxis: {
        type: "value",
        min: 0,
        axisLabel: {
          formatter: (value: number) => {
            if (value >= 1000) return value / 1000 + "k";
            return value;
          },
          color: "#9CA3AF",
        },
        splitLine: {
          lineStyle: {
            type: "dashed",
            color: "#E5E7EB",
          },
        },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      series: [
        {
          name: "Consumption",
          type: "line",
          smooth: true,
          symbol: "none",
          lineStyle: {
            width: 2,
            color: "#1064ED",
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: "rgba(16, 100, 237, 0.2)" },
              { offset: 1, color: "rgba(16, 100, 237, 0)" },
            ]),
          },
          data: chartData.values,
        },
      ],
    };
  }, []);

  return (
    <ReactECharts option={options} style={{ height: "100%", width: "100%" }} />
  );
};
